import React from 'react';
import { 
  Paper, 
  Typography, 
  Box,
  LinearProgress,
  Chip,
  Tooltip
} from '@material-ui/core';
import { 
  TrendingUp,
  Schedule,
  DirectionsCar,
  Work,
  Warning,
  CheckCircle
} from '@material-ui/icons';
import moment from 'moment';
import { useStyles } from '../DailyScheduleMapViewStyles';

const WeeklySummaryPanel = ({ 
  weeklyRoutes, 
  crews, 
  sites,
  selectedDate 
}) => {
  const classes = useStyles();

  // Calculate weekly metrics
  const weeklyMetrics = React.useMemo(() => {
    if (!weeklyRoutes || !weeklyRoutes.length) return null;

    let totalStops = 0;
    let totalMiles = 0;
    let totalDriveTime = 0;
    let totalServiceTime = 0;
    let crewUtilization = {};
    let dailyStats = {};

    // Initialize crew utilization
    crews.forEach(crew => {
      crewUtilization[crew._id] = {
        name: crew.name,
        color: crew.color,
        totalHours: 0,
        days: 0
      };
    });

    // Process each day
    weeklyRoutes.forEach(dayRoute => {
      let dayStops = 0;
      let dayMiles = 0;
      let dayHours = 0;

      dayRoute.routes?.forEach(route => {
        dayStops += route.stops?.length || 0;
        dayMiles += route.totalMilesDriven || 0;
        totalDriveTime += route.totalDriveTime || 0;
        totalServiceTime += route.totalServiceTime || 0;
        
        const totalTime = ((route.totalDriveTime || 0) + (route.totalServiceTime || 0)) / 60;
        dayHours += totalTime;

        if (crewUtilization[route.crewId]) {
          crewUtilization[route.crewId].totalHours += totalTime;
          crewUtilization[route.crewId].days += 1;
        }
      });

      totalStops += dayStops;
      totalMiles += dayMiles;
      
      dailyStats[dayRoute.day] = {
        stops: dayStops,
        miles: Math.round(dayMiles),
        hours: Math.round(dayHours)
      };
    });

    // Calculate unassigned sites
    const unassignedCount = sites.filter(site => !site.crew).length;
    const assignmentRate = sites.length > 0 ? 
      Math.round(((sites.length - unassignedCount) / sites.length) * 100) : 0;

    return {
      totalStops,
      totalMiles: Math.round(totalMiles),
      totalDriveTime: Math.round(totalDriveTime / 60), // Convert to hours
      totalServiceTime: Math.round(totalServiceTime / 60), // Convert to hours
      avgStopsPerDay: Math.round(totalStops / 5), // Assuming 5 work days
      avgMilesPerDay: Math.round(totalMiles / 5),
      unassignedCount,
      assignmentRate,
      crewUtilization: Object.values(crewUtilization).filter(c => c.days > 0),
      dailyStats
    };
  }, [weeklyRoutes, crews, sites]);

  if (!weeklyMetrics) {
    return (
      <Paper className={classes.weeklySummaryPanel}>
        <Typography variant="subtitle2" color="textSecondary">
          Loading weekly summary...
        </Typography>
      </Paper>
    );
  }

  const currentDay = moment(selectedDate).format('dddd');
  const todayStats = weeklyMetrics.dailyStats[currentDay];

  return (
    <Paper className={classes.weeklySummaryPanel}>
      <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 'bold' }}>
        Weekly Summary
      </Typography>

      {/* Today's Highlight */}
      {todayStats && (
        <Box mb={1} p={1} bgcolor="primary.light" borderRadius={1}>
          <Typography variant="caption" style={{ fontWeight: 'bold' }}>
            Today ({currentDay})
          </Typography>
          <Box display="flex" justifyContent="space-between" mt={0.5}>
            <Chip size="small" label={`${todayStats.stops} stops`} />
            <Chip size="small" label={`${todayStats.miles} mi`} />
            <Chip size="small" label={`${todayStats.hours}h`} />
          </Box>
        </Box>
      )}

      {/* Weekly Totals */}
      <Box className={classes.summaryRow}>
        <Box className={classes.summaryMetric}>
          <Work fontSize="small" color="primary" />
          <Typography variant="caption">
            {weeklyMetrics.totalStops} stops
          </Typography>
        </Box>
        <Typography variant="caption" color="textSecondary">
          ~{weeklyMetrics.avgStopsPerDay}/day
        </Typography>
      </Box>

      <Box className={classes.summaryRow}>
        <Box className={classes.summaryMetric}>
          <DirectionsCar fontSize="small" color="primary" />
          <Typography variant="caption">
            {weeklyMetrics.totalMiles} miles
          </Typography>
        </Box>
        <Typography variant="caption" color="textSecondary">
          ~{weeklyMetrics.avgMilesPerDay}/day
        </Typography>
      </Box>

      <Box className={classes.summaryRow}>
        <Box className={classes.summaryMetric}>
          <Schedule fontSize="small" color="primary" />
          <Typography variant="caption">
            {weeklyMetrics.totalServiceTime}h work
          </Typography>
        </Box>
        <Typography variant="caption" color="textSecondary">
          {weeklyMetrics.totalDriveTime}h drive
        </Typography>
      </Box>

      {/* Assignment Status */}
      <Box mt={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Typography variant="caption">Site Assignment</Typography>
          <Typography variant="caption" color="primary">
            {weeklyMetrics.assignmentRate}%
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={weeklyMetrics.assignmentRate} 
          style={{ height: 6, borderRadius: 3 }}
        />
        <Box display="flex" justifyContent="space-between" mt={0.5}>
          <Typography variant="caption" color="textSecondary">
            {sites.length - weeklyMetrics.unassignedCount} assigned
          </Typography>
          <Typography variant="caption" color="error">
            {weeklyMetrics.unassignedCount} unassigned
          </Typography>
        </Box>
      </Box>

      {/* Crew Utilization */}
      <Box mt={1}>
        <Typography variant="caption" style={{ fontWeight: 'bold' }}>
          Crew Utilization
        </Typography>
        {weeklyMetrics.crewUtilization.map(crew => {
          const avgHoursPerDay = crew.days > 0 ? (crew.totalHours / crew.days).toFixed(1) : 0;
          const utilizationPercent = Math.min((avgHoursPerDay / 8) * 100, 100);
          
          return (
            <Tooltip 
              key={crew.name}
              title={`${crew.totalHours.toFixed(1)}h total / ${crew.days} days`}
            >
              <Box mt={0.5}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" style={{ color: crew.color }}>
                    {crew.name}
                  </Typography>
                  <Typography variant="caption">
                    {avgHoursPerDay}h/day
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={utilizationPercent}
                  style={{ 
                    height: 4, 
                    borderRadius: 2,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: crew.color
                    }
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* Alerts */}
      {weeklyMetrics.unassignedCount > 10 && (
        <Box mt={1} p={0.5} bgcolor="error.light" borderRadius={1}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Warning fontSize="small" color="error" />
            <Typography variant="caption">
              {weeklyMetrics.unassignedCount} sites need assignment
            </Typography>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default WeeklySummaryPanel;